import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkle, Copy, WhatsappLogo, Envelope, Lightbulb, Package, User, Notepad, ChartLine, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

/* ---------- Legacy free-text generator (unchanged endpoint) ---------- */
const FREE_TASKS = [
  { key: "followup", label: "Follow-up", icon: WhatsappLogo },
  { key: "email", label: "Email", icon: Envelope },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
  { key: "summary", label: "Customer Summary", icon: User },
];

function FreeText() {
  const [task, setTask] = useState("followup");
  const [customerName, setCustomerName] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!context.trim()) return toast.error("Add some context");
    setLoading(true); setResult("");
    try {
      const r = await api.post("/ai/generate", { task, context, customer_name: customerName });
      setResult(r.data.text);
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <div className="overline text-muted-foreground mb-3">Input</div>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {FREE_TASKS.map(t => (
              <button key={t.key} onClick={() => setTask(t.key)}
                data-testid={`ai-task-${t.key}`}
                className={`px-3 py-1.5 rounded-full text-xs border ${task === t.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <t.icon size={12} weight="duotone" className="inline mr-1" />{t.label}
              </button>
            ))}
          </div>
          <div>
            <Label className="text-xs">CUSTOMER NAME (optional)</Label>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="ai-customer-input" />
          </div>
          <div>
            <Label className="text-xs">CONTEXT</Label>
            <Textarea rows={9} value={context} onChange={(e) => setContext(e.target.value)}
              placeholder="e.g., Customer ordered crystal chandeliers 6 months ago and wants to reorder for a Feb wedding."
              data-testid="ai-context-input" />
          </div>
          <Button onClick={generate} disabled={loading} className="w-full rounded-full" data-testid="ai-generate-button">
            <Sparkle size={16} weight="fill" className="mr-2" />
            {loading ? "Generating…" : "Generate with AI"}
          </Button>
        </div>
      </Card>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-muted-foreground">Output</div>
          {result && (
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result); toast.success("Copied"); }} data-testid="ai-copy-button">
              <Copy size={12} className="mr-1" />Copy
            </Button>
          )}
        </div>
        <div className="min-h-[280px] whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-output">
          {loading ? <div className="text-muted-foreground animate-pulse">Thinking…</div>
            : result || <div className="text-muted-foreground">Your generated message will appear here.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ---------- Shared customer picker ---------- */
function useCustomers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/customers").then(r => setItems(r.data)).catch(() => {}); }, []);
  return items;
}

/* ---------- Product Recommendation ---------- */
function Recommendation() {
  const customers = useCustomers();
  const [customerId, setCustomerId] = useState("");
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.post("/ai/recommend", { customer_id: customerId || null, brief });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="rec-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div><Label className="text-xs">CUSTOMER (optional)</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="rec-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">BRIEF</Label>
            <Input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g., wedding season restock" data-testid="rec-brief" />
          </div>
        </div>
        <Button onClick={run} disabled={loading} className="mt-4 rounded-full" data-testid="rec-run">
          <Sparkle size={14} weight="fill" className="mr-2" />{loading ? "Analysing…" : "Recommend products"}
        </Button>
      </Card>
      {data && !data._parse_error && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="rec-results">
          {(data.recommendations || []).map((r, i) => (
            <Card key={i} className="p-5 hover-lift">
              <div className="flex items-start justify-between mb-2">
                <Package size={18} weight="duotone" className="text-primary" />
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {Math.round((r.confidence || 0) * 100)}% match
                </span>
              </div>
              <div className="font-heading font-bold text-lg leading-tight">{r.name}</div>
              {r.category && <div className="text-xs text-muted-foreground mt-1">{r.category}</div>}
              {r.price != null && <div className="font-heading text-xl font-bold mt-3">₹{(r.price || 0).toLocaleString("en-IN")}</div>}
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{r.reason}</p>
            </Card>
          ))}
          {data.notes && <Card className="p-5 md:col-span-2 xl:col-span-3 border-dashed">
            <div className="overline text-muted-foreground mb-2">Notes</div>
            <p className="text-sm">{data.notes}</p>
          </Card>}
        </div>
      )}
      {data?._parse_error && <Card className="p-5 text-sm text-muted-foreground">AI returned an unparseable response. Try again with a clearer brief.</Card>}
    </div>
  );
}

/* ---------- Quote Generator ---------- */
function QuoteGen() {
  const customers = useCustomers();
  const [customerId, setCustomerId] = useState("");
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  const run = async () => {
    if (!customerId) return toast.error("Pick a customer");
    if (!brief.trim()) return toast.error("Add a brief");
    setLoading(true); setQuote(null);
    try {
      const r = await api.post("/ai/quote", { customer_id: customerId, brief });
      setQuote(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  const saveQuote = async () => {
    if (!quote?.items?.length) return;
    const cust = customers.find(c => c.id === customerId);
    try {
      await api.post("/quotations", {
        customer_id: customerId, customer_name: cust?.customer_name || "",
        items: quote.items.map(i => ({ product_id: i.product_id, name: i.name, qty: i.qty, price: i.price, gst: i.gst })),
        discount: quote.discount || 0, notes: quote.cover_message || quote.notes || "", status: "draft",
      });
      toast.success("Draft quotation created");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="quote-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div><Label className="text-xs">CUSTOMER</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="quote-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">BRIEF</Label>
            <Input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g., 200-guest wedding, mid-Feb, budget ₹3-5L" data-testid="quote-brief" />
          </div>
        </div>
        <Button onClick={run} disabled={loading} className="mt-4 rounded-full" data-testid="quote-run">
          <Sparkle size={14} weight="fill" className="mr-2" />{loading ? "Drafting…" : "Draft a quotation"}
        </Button>
      </Card>
      {quote && !quote._parse_error && (
        <Card className="p-6" data-testid="quote-result">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-muted-foreground">Draft quotation</div>
              <h3 className="font-heading font-bold text-xl mt-1">Suggested line items</h3>
            </div>
            <Button onClick={saveQuote} data-testid="quote-save" className="rounded-full">
              <Receipt size={14} className="mr-2" />Save as draft
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border/60">
              <tr><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Price</th><th className="text-right py-2">GST</th><th className="text-left py-2 pl-4">Why</th></tr>
            </thead>
            <tbody>
              {(quote.items || []).map((it, i) => (
                <tr key={i} className="border-b border-border/40" data-testid={`quote-item-${i}`}>
                  <td className="py-2 font-medium">{it.name}</td>
                  <td className="py-2 text-right">{it.qty}</td>
                  <td className="py-2 text-right">₹{(it.price || 0).toLocaleString("en-IN")}</td>
                  <td className="py-2 text-right">{it.gst}%</td>
                  <td className="py-2 pl-4 text-xs text-muted-foreground">{it.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 pt-4 border-t border-border/60 flex justify-between items-start">
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">{quote.cover_message}</p>
            <div className="text-right space-y-1">
              <div className="text-xs text-muted-foreground">Subtotal: ₹{(quote.subtotal || 0).toLocaleString("en-IN")}</div>
              <div className="text-xs text-muted-foreground">GST: ₹{(quote.gst_amount || 0).toLocaleString("en-IN")}</div>
              {quote.discount ? <div className="text-xs text-muted-foreground">Discount: -₹{quote.discount.toLocaleString("en-IN")}</div> : null}
              <div className="font-heading font-bold text-2xl">₹{(quote.total || 0).toLocaleString("en-IN")}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------- Sales Suggestions ---------- */
function Suggestions() {
  const customers = useCustomers();
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const priTone = { high: "bg-destructive/10 text-destructive border-destructive/30",
                    medium: "bg-primary/10 text-primary border-primary/30",
                    low: "bg-muted text-muted-foreground border-border" };

  const run = async () => {
    if (!customerId) return toast.error("Pick a customer");
    setLoading(true); setData(null);
    try {
      const r = await api.post("/ai/suggest", { customer_id: customerId });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="sug-card">
        <div className="flex gap-3 items-end">
          <div className="flex-1"><Label className="text-xs">CUSTOMER</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="sug-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={run} disabled={loading} className="rounded-full" data-testid="sug-run">
            <Sparkle size={14} weight="fill" className="mr-2" />{loading ? "Analysing…" : "Get suggestions"}
          </Button>
        </div>
      </Card>
      {data && !data._parse_error && (
        <div className="space-y-4" data-testid="sug-results">
          {data.summary && <Card className="p-5 bg-primary/5 border-primary/30">
            <div className="overline text-primary mb-2">AI summary</div>
            <p className="text-sm leading-relaxed">{data.summary}</p>
          </Card>}
          {(data.suggestions || []).map((s, i) => (
            <Card key={i} className="p-5" data-testid={`sug-item-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-heading font-bold">{s.title}</h4>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${priTone[s.priority] || priTone.medium}`}>{s.priority}</span>
              </div>
              <p className="text-sm mt-2"><span className="font-semibold">Action:</span> {s.action}</p>
              {s.why && <p className="text-xs text-muted-foreground mt-2 italic">{s.why}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Sales Forecast ---------- */
function Forecast() {
  const [months, setMonths] = useState(3);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true); setData(null);
    try { const r = await api.post("/ai/forecast", { months_ahead: +months }); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="fc-card">
        <div className="flex gap-3 items-end">
          <div><Label className="text-xs">MONTHS AHEAD</Label>
            <Select value={String(months)} onValueChange={(v) => setMonths(+v)}>
              <SelectTrigger className="w-32" data-testid="fc-months"><SelectValue /></SelectTrigger>
              <SelectContent>{[1,2,3,4,5,6].map(n => <SelectItem key={n} value={String(n)}>{n} months</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={run} disabled={loading} className="rounded-full" data-testid="fc-run">
            <Sparkle size={14} weight="fill" className="mr-2" />{loading ? "Forecasting…" : "Run forecast"}
          </Button>
        </div>
      </Card>
      {data && !data._parse_error && (
        <div className="space-y-6" data-testid="fc-results">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5">
              <div className="overline text-muted-foreground">Growth vs recent</div>
              <div className="font-heading text-3xl font-bold mt-2">{(data.growth_pct || 0) > 0 ? "+" : ""}{Math.round(data.growth_pct || 0)}%</div>
            </Card>
            <Card className="p-5">
              <div className="overline text-muted-foreground">Forecast horizon</div>
              <div className="font-heading text-3xl font-bold mt-2">{data.months_ahead} mo</div>
            </Card>
            <Card className="p-5">
              <div className="overline text-muted-foreground">Total projected</div>
              <div className="font-heading text-3xl font-bold mt-2">₹{Math.round((data.forecast || []).reduce((s, f) => s + (f.expected_sales || 0), 0)).toLocaleString("en-IN")}</div>
            </Card>
          </div>
          <Card className="p-6">
            <div className="overline text-muted-foreground mb-3">Monthly forecast</div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border/60">
                <tr><th className="text-left py-2">Month</th><th className="text-right py-2">Expected ₹</th><th className="text-right py-2">Confidence</th><th className="text-left py-2 pl-4">Reasoning</th></tr>
              </thead>
              <tbody>
                {(data.forecast || []).map((f, i) => (
                  <tr key={i} className="border-b border-border/40" data-testid={`fc-row-${i}`}>
                    <td className="py-2 font-mono">{f.month}</td>
                    <td className="py-2 text-right font-heading font-bold">₹{(f.expected_sales || 0).toLocaleString("en-IN")}</td>
                    <td className="py-2 text-right">{Math.round((f.confidence || 0) * 100)}%</td>
                    <td className="py-2 pl-4 text-xs text-muted-foreground">{f.reasoning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="overline text-destructive mb-2">Risks</div>
              <ul className="space-y-2 text-sm">{(data.risks || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-destructive">•</span>{r}</li>)}</ul>
            </Card>
            <Card className="p-5">
              <div className="overline text-emerald-600 mb-2">Opportunities</div>
              <ul className="space-y-2 text-sm">{(data.opportunities || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">•</span>{r}</li>)}</ul>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Root ---------- */
export default function AIAssistant() {
  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="overline text-muted-foreground mb-2">Powered by Claude Sonnet 4.5</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">AI Assistant</h1>
        <p className="text-sm text-muted-foreground mt-2">Draft messages, forecast sales, and get context-aware recommendations powered by your own CRM data.</p>
      </div>
      <Tabs defaultValue="recommend" className="w-full">
        <TabsList className="w-full flex flex-wrap gap-1 justify-start" data-testid="ai-tabs">
          <TabsTrigger value="recommend" data-testid="tab-recommend"><Package size={14} weight="duotone" className="mr-1.5" />Recommend</TabsTrigger>
          <TabsTrigger value="quote" data-testid="tab-quote"><Notepad size={14} weight="duotone" className="mr-1.5" />Quote Generator</TabsTrigger>
          <TabsTrigger value="suggest" data-testid="tab-suggest"><Lightbulb size={14} weight="duotone" className="mr-1.5" />Sales Suggestions</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast"><ChartLine size={14} weight="duotone" className="mr-1.5" />Sales Forecast</TabsTrigger>
          <TabsTrigger value="write" data-testid="tab-write"><Envelope size={14} weight="duotone" className="mr-1.5" />Free Text</TabsTrigger>
        </TabsList>
        <TabsContent value="recommend" className="mt-6"><Recommendation /></TabsContent>
        <TabsContent value="quote" className="mt-6"><QuoteGen /></TabsContent>
        <TabsContent value="suggest" className="mt-6"><Suggestions /></TabsContent>
        <TabsContent value="forecast" className="mt-6"><Forecast /></TabsContent>
        <TabsContent value="write" className="mt-6"><FreeText /></TabsContent>
      </Tabs>
    </div>
  );
}
